import { type PropsWithChildren } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { IconLink, theme, useResponsive } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationFloatingControls = (props: Props) => {
  const { children, variant = 'legacy', shouldDisableControls, onBackButtonPress } = props;

  const { formatMessage } = useIntl();
  const { currentBreakpoint } = useResponsive();

  // ─────────────────────────────────────────────────────────────────────

  const shouldRenderBackButton = variant === 'new' && currentBreakpoint.isXS;

  // ─────────────────────────────────────────────────────────────────────

  if (!shouldRenderBackButton) {
    return children;
  }

  return (
    <View style={styles.container}>
      <IconLink
        name="IconArrowLeft"
        palette="white"
        iconSize={24}
        width={56}
        height={56}
        accessibilityLabel={formatMessage(messages.backButtonA11yLabel)}
        disabled={shouldDisableControls}
        style={styles.backButton}
        onPress={onBackButtonPress}
      />

      <View style={styles.ctaContainer}>{children}</View>
    </View>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  backButtonA11yLabel: {
    defaultMessage: 'Go back',
    description: 'Customization | Floating controls | Back button label',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing['4'],
  },
  ctaContainer: {
    flexShrink: 1,
    marginLeft: 'auto',
  },
  backButton: {
    boxShadow: '0px 0px 20px 0px rgba(14, 21, 14, 0.05)',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = PropsWithChildren<{
  variant?: 'legacy' | 'new';
  shouldDisableControls?: boolean;
  onBackButtonPress: () => void;
}>;
